import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { Viewport } from '@tma.js/sdk'
import type {TGeneration} from '../../../shared/types/GenerationType'
import type {Theme} from "@mui/material/styles";
import {PlatformType} from "../../../shared/types/PlatformType.ts";
import { ColorsTheme } from "../../../shared/data/colors.ts";
import { updateGenerations, replaceOptimisticGeneration } from './userSlice.ts';


interface PopUpMessage {
    isOpen: boolean;
    message: string | null;
}


interface AppState {
  viewport: Viewport | null
  fullscreenPreview: TGeneration | null
  isPaymentOpen: boolean
  isPopUpOpen: PopUpMessage
  theme: Theme | null
  platform: PlatformType
  safeArea: {top: number, bottom: number}
  colors: string[]

}

const initialState: AppState = {
  viewport: null,
  fullscreenPreview: null,
  isPaymentOpen: false,
  isPopUpOpen: {isOpen: false, message: null },
  theme: null,
  platform: 'unknown',
  safeArea: {top: 10, bottom: 0},
  colors: ColorsTheme[0]
}

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setViewport: (state, action: PayloadAction<Viewport>) => {
      state.viewport = action.payload
    },
    setFullscreenPreview: (state, action: PayloadAction<TGeneration | null>) => {
      state.fullscreenPreview = action.payload
    },
    setPaymentOpen: (state, action: PayloadAction<boolean>) => {
      state.isPaymentOpen = action.payload
    },
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload as any
    },
    setPlatform: (state, action: PayloadAction<PlatformType>) => {
      state.platform = action.payload
    },
    setColors: (state, action: PayloadAction<string[]>) => {
      state.colors = action.payload
    },
    setSafeAreaStore: (state, action: PayloadAction<{top: number, bottom: number}>) => {
      state.safeArea = action.payload
    },
    setPopUpOpen: (state, action: PayloadAction<PopUpMessage>) => {
      state.isPopUpOpen = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(updateGenerations, (state, action) => {
      if (!state.fullscreenPreview) return;
      const updated = action.payload.find((g) => g.id === state.fullscreenPreview!.id);
      if (updated) {
        state.fullscreenPreview = { ...state.fullscreenPreview, ...updated };
      }
    });
    builder.addCase(replaceOptimisticGeneration, (state, action) => {
      if (!state.fullscreenPreview) return;
      const { optimisticId, generation } = action.payload;
      if (state.fullscreenPreview.id === optimisticId) {
        state.fullscreenPreview = generation;
      }
    });
  },
})

export const { setViewport, setFullscreenPreview, setPaymentOpen, setTheme, setPlatform, setColors, setSafeAreaStore, setPopUpOpen } = appSlice.actions

export default appSlice.reducer
